import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Dialog, Form } from "~/components/ui";
import { useCallback, useEffect, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { LexiconSubject } from "~/common/lexicon-subject";
import { useCreateLexiconConcept } from "@noa/api-client";
import type { AppLanguageType } from "@noa/i18n";
import {
  firstTabWithError,
  fullLexicalConceptFormToRequestBody,
  lexiconConceptSchema,
  lexiconModalClassNames,
} from "~/utils/lexicon";
import type { LexiconConceptForm } from "~/utils/lexicon";
import { LexiconConceptFields } from "./lexicon-concept-fields";

type CreateConceptModalProps = {
  isOpen: boolean;
  language: AppLanguageType;
  selectedSubjectId?: LexiconSubject["id"] | null;
  subjects: LexiconSubject[];
  closeModal: () => void;
};

export function CreateConceptModal({
  isOpen,
  language,
  selectedSubjectId,
  subjects,
  closeModal,
}: CreateConceptModalProps) {
  const { t } = useTranslation("lexicon");
  const tabsKey = `${selectedSubjectId ?? ""}-${language}`;
  const [activeTab, setActiveTab] = useState<string>(language);
  const [submitError, setSubmitError] = useState<string>();
  const { mutate: createLexicalConcept, isPending: isCreatingLexicalConcept } = useCreateLexiconConcept();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LexiconConceptForm>({ resolver: zodResolver(lexiconConceptSchema) });

  const onSubmit = useCallback(
    (values: LexiconConceptForm) => {
      setSubmitError(undefined);

      const body = fullLexicalConceptFormToRequestBody(values, subjects);

      // Backstop: the options are pre-filtered to bilingual subjects, so this is
      // only reachable if one lost a translation between load and submit. Say so
      // — returning silently is what made the OK button look dead.
      if (!body) {
        setSubmitError(t("errors.subjectNotTranslated"));
        return;
      }

      createLexicalConcept(body, {
        onSuccess: () => {
          closeModal();
          reset();
        },
        onError: () => setSubmitError(t("errors.submitFailed")),
      });
    },
    [subjects, createLexicalConcept, closeModal, reset, t]
  );

  // Validation errors on a hidden tab are invisible, so a plain OK looks like a
  // dead button. Reveal the offending tab instead. Takes the errors react-hook-form
  // passes: the `formState` ones are still empty this tick, on the first submit.
  const revealInvalidTab = useCallback(
    (submitErrors: FieldErrors<LexiconConceptForm>) => {
      const target = firstTabWithError(submitErrors, activeTab === "en" ? "en" : "fr");
      if (target) setActiveTab(target);
    },
    [activeTab]
  );

  const handleCancel = useCallback(() => {
    closeModal();
    reset();
    setSubmitError(undefined);
  }, [closeModal, reset]);

  useEffect(() => {
    if (selectedSubjectId) {
      setValue("subject_id", selectedSubjectId);
    }
  }, [selectedSubjectId, setValue]);

  // Now that the tab is controlled, `tabsKey`'s remount no longer resets it —
  // follow the app language ourselves so a language switch still lands on it.
  useEffect(() => {
    setActiveTab(language);
  }, [language]);

  return (
    <Dialog
      title={t("createConceptModal.title")}
      open={isOpen}
      onOk={handleSubmit(onSubmit, revealInvalidTab)}
      onCancel={handleCancel}
      okText={t("actions.create")}
      cancelText={t("common:cancel")}
      confirmLoading={isCreatingLexicalConcept}
      classNames={lexiconModalClassNames}
    >
      <Form>
        <LexiconConceptFields
          control={control}
          errors={errors}
          language={language}
          subjects={subjects}
          tabsKey={tabsKey}
          activeKey={activeTab}
          onTabChange={setActiveTab}
        />
        {submitError && <Alert type="error" message={submitError} showIcon className="mt-2" />}
      </Form>
    </Dialog>
  );
}
